
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LuxuryButton from "@/components/ui/luxury-button";
import { Card, CardContent } from "@/components/ui/card";
import { Check } from 'lucide-react';

const PartnerCTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    location: '',
    practiceInfo: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Partner application submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const nextSteps = [
    "We review your application within 24 hours",
    "Schedule a 30-minute strategy call", 
    "If approved, go live in 30 days"
  ];

  const requirements = [
    "Established dental practice (2+ years)",
    "500+ active patients",
    "Available treatment room or space",
    "Commitment to modern patient experience"
  ];

  return (
    <section id="qualify" className="py-32 bg-gradient-to-br from-juv-deep-chestnut to-juv-terracotta">
      <div className="container mx-auto px-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-serif font-light text-white mb-8">
            Apply to Become a JUV Partner
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-juv-deep-chestnut mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-juv-terracotta focus:border-transparent"
                        placeholder="Dr. John Smith"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-juv-deep-chestnut mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-juv-terracotta focus:border-transparent"
                        placeholder="doctor@practice.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-juv-deep-chestnut mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-juv-terracotta focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-juv-deep-chestnut mb-2">
                        Practice Name *
                      </label>
                      <input
                        type="text"
                        name="practiceName"
                        value={formData.practiceName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-juv-terracotta focus:border-transparent"
                        placeholder="Smile Dental Group"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-juv-deep-chestnut mb-2">
                      Location (City, State) *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-juv-terracotta focus:border-transparent"
                      placeholder="Beverly Hills, CA"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-juv-deep-chestnut mb-2">
                      Tell us about your practice
                    </label>
                    <textarea
                      name="practiceInfo"
                      value={formData.practiceInfo}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-juv-terracotta focus:border-transparent"
                      placeholder="Number of operatories, patient volume, current services offered..."
                    />
                  </div>

                  <LuxuryButton
                    type="submit"
                    size="lg"
                    variant="primary"
                    glowEffect
                    className="w-full py-4 text-lg font-medium"
                  >
                    Apply Now
                  </LuxuryButton>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Next Steps & Requirements */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
              <h4 className="text-2xl font-semibold text-white mb-6">
                What Happens Next?
              </h4>
              <div className="space-y-4 text-white/90">
                {nextSteps.map((step, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-juv-rose-clay rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
              <h4 className="text-xl font-semibold text-white mb-4">
                Partner Requirements
              </h4>
              <div className="space-y-3">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-juv-rose-clay" />
                    <span className="text-white/90">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PartnerCTA;
